import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { BottomNav } from "../components/BottomNav";
import type { ShareToken } from "../types";

function uploadUrlForToken(token: string): string {
  return `${window.location.origin}/api/v1/share/${token}/upload`;
}

export function ShareSetupPage() {
  const [tokens, setTokens] = useState<ShareToken[]>([]);
  const [freshUrl, setFreshUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function reload() {
    const rows = await api.request<ShareToken[]>("/share-tokens");
    setTokens(rows);
  }

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load share links");
      }
    })();
  }, []);

  async function createToken() {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const created = await api.request<ShareToken>("/share-tokens", {
        method: "POST",
        body: JSON.stringify({ label: "iPhone Share" }),
      });
      if (created.token) {
        setFreshUrl(uploadUrlForToken(created.token));
      }
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create share link");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: number) {
    if (!confirm("Revoke this share link? Existing Shortcuts using it will stop working.")) {
      return;
    }
    setBusy(true);
    try {
      await api.request(`/share-tokens/${id}`, { method: "DELETE" });
      if (freshUrl?.includes("/share/")) {
        // Keep showing URL until user creates a new one — they may still need it
      }
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not revoke");
    } finally {
      setBusy(false);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setError("Could not copy — select the URL manually.");
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/capture" className="muted">
          ← Capture
        </Link>
      </header>

      <div className="panel stack">
        <div>
          <h2 style={{ fontSize: "1.35rem", margin: 0 }}>Share from iPhone</h2>
          <p className="muted" style={{ margin: "0.4rem 0 0" }}>
            Apple does not let websites appear in the Share sheet. Use a one-time
            Shortcuts setup instead — then Photos, Mail, and Files can send
            receipts straight here.
          </p>
        </div>

        {error && <div className="alert">{error}</div>}

        <button
          type="button"
          className="btn btn-accent btn-block"
          disabled={busy}
          onClick={createToken}
        >
          {busy ? "Creating…" : tokens.length ? "Create new share link" : "Create share link"}
        </button>

        {freshUrl && (
          <div className="stack" style={{ gap: "0.5rem" }}>
            <div className="muted" style={{ fontSize: "0.8rem" }}>
              Copy this URL into your Shortcut (shown only once):
            </div>
            <code className="share-url">{freshUrl}</code>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => copy(freshUrl)}
            >
              {copied ? "Copied" : "Copy upload URL"}
            </button>
          </div>
        )}
      </div>

      <div className="panel stack" style={{ marginTop: "0.85rem" }}>
        <h3 style={{ fontSize: "1rem", margin: 0 }}>Set up the Shortcut</h3>
        <ol className="share-steps">
          <li>Create a share link above and copy the URL.</li>
          <li>
            Open the <strong>Shortcuts</strong> app → <strong>+</strong> → name it
            “Send to Receipts”.
          </li>
          <li>
            Add action <strong>Get Contents of URL</strong>.
            <ul>
              <li>URL → paste the copied link</li>
              <li>Method → <strong>POST</strong></li>
              <li>Request Body → <strong>Form</strong></li>
              <li>Add field <code>file</code> → type <strong>File</strong> → value{" "}
                <strong>Shortcut Input</strong></li>
            </ul>
          </li>
          <li>
            Tap the ⓘ info button → enable <strong>Show in Share Sheet</strong> →
            accept <strong>Images</strong> and <strong>PDFs</strong> (Files).
          </li>
          <li>
            From Photos or Mail: Share → <strong>Send to Receipts</strong>.
          </li>
        </ol>
        <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
          Without a Shortcut you can still use Capture → Photos / Files, or copy a
          photo and paste it on the Capture screen.
        </p>
      </div>

      {tokens.length > 0 && (
        <div className="panel stack" style={{ marginTop: "0.85rem" }}>
          <h3 style={{ fontSize: "1rem", margin: 0 }}>Your share links</h3>
          {tokens.map((t) => (
            <div key={t.id} className="row between" style={{ alignItems: "flex-start" }}>
              <div>
                <div>{t.label || "Share link"}</div>
                <div className="muted mono" style={{ fontSize: "0.8rem" }}>
                  {t.token_preview}
                  {t.last_used_at
                    ? ` · last used ${new Date(t.last_used_at).toLocaleString()}`
                    : " · unused"}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => revoke(t.id)}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
